import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api/api';
import './TrackProjectView.css';

/**
 * TrackProjectView
 * Highly techy project mission page for Learnlia Tracks.
 */
function TrackProjectView() {
  const { slug, phaseId } = useParams();
  const navigate = useNavigate();

  // eslint-disable-next-line no-unused-vars
  const [track, setTrack] = useState(null);
  const [phase, setPhase] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [projectUrl, setProjectUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitMsg, setSubmitMsg] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await api.get(`/tracks/${slug}`);
        const t = res.data.track;
        const p = t.phases.find(ph => ph._id === phaseId);

        if (!p) throw new Error("Phase not found");
        if (!p.project) throw new Error("No project found for this phase");

        setTrack(t);
        setPhase(p);
        setProject(p.project);
        setProjectUrl(p.phaseProgress?.projectUrl || '');
        setLoading(false);
      } catch (err) {
        setError(err.message || "Failed to load project details");
        setLoading(false);
      }
    };
    fetchData();
  }, [slug, phaseId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!projectUrl.trim()) return;

    setSubmitting(true);
    setSubmitMsg(null);
    try {
      await api.post(`/tracks/${slug}/complete-phase`, {
        phaseId,
        projectUrl: projectUrl.trim()
      });
      setSubmitMsg({ type: 'success', text: "Mission accomplished! Project submitted." });
      // Refresh track data to show completion
      setTimeout(() => navigate(`/tracks/${slug}`), 2000);
    } catch (err) {
      setSubmitMsg({ type: 'error', text: err.response?.data?.message || "Transmission failed. Try again." });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="tpv-page tpv-page--loading"><span className="tpv-spinner" />Loading mission brief...</div>;
  if (error) return <div className="tpv-page tpv-page--error"><div className="tpv-error-card"><h2>Connection Lost</h2><p>{error}</p><button onClick={() => navigate(`/tracks/${slug}`)}>Abnormal Exit</button></div></div>;

  const pp = phase.phaseProgress;
  const isDone = pp?.completed;
  const quizScore = pp?.quizScore || 0;

  return (
    <div className="tpv-page">
      <header className="tpv-header">
        <button className="tpv-back-link" onClick={() => navigate(`/tracks/${slug}`)}>
          ← BACK_TO_CURRICULUM
        </button>
        <div className="tpv-header-main">
          <span className="tpv-subtitle">PHASE MILESTONE // 0{phase.order}</span>
          <h1 className="tpv-main-title">{project.title}</h1>
        </div>
      </header>

      <div className="tpv-layout">
        <div className="tpv-main-content">
          <section className="tpv-card tpv-brief animate-slide-up">
            <div className="card-lbl">MISSION BRIEFING</div>
            <div className="tpv-brief-header">
              <span className="tpv-mission-code">OBJ_PHASE_{phase.order}</span>
              <h2 className="tpv-card-title">Strategic Objective</h2>
            </div>
            <p className="tpv-description">{project.description}</p>

            <div className="tpv-details-grid">
              <div className="tpv-detail-item">
                <label>Complexity</label>
                <div className="tpv-comp-meter">
                  {[1, 2, 3, 4, 5].map(i => (
                    <div key={i} className={`tpv-comp-bar ${i <= phase.order ? 'active' : ''}`} />
                  ))}
                  <span>Level {phase.order}</span>
                </div>
              </div>
              <div className="tpv-detail-item">
                <label>Target Duration</label>
                <span>4 - 6 Study Hours</span>
              </div>
            </div>
          </section>

          <section className="tpv-card tpv-criteria animate-slide-up" style={{ animationDelay: '0.1s' }}>
            <div className="card-lbl">VERIFICATION PROTOCOLS</div>
            <div className="tpv-terminal">
              <div className="tpv-terminal-header">
                <span className="dot" /> <span className="dot" /> <span className="dot" />
                <span className="tpv-terminal-title">validation_engine.v1</span>
              </div>
              <div className="tpv-terminal-body">
                <div className="tpv-term-line">
                  <span className="tpv-term-cmd">&gt; status check --core_features</span>
                  <span className="tpv-term-res">[REQUIRED] Implementation of all primary modules.</span>
                </div>
                <div className="tpv-term-line">
                  <span className="tpv-term-cmd">&gt; inspect --code_quality</span>
                  <span className="tpv-term-res">[REQUIRED] Adherence to clean coding standards.</span>
                </div>
                <div className="tpv-term-line">
                  <span className="tpv-term-cmd">&gt; check --deployment</span>
                  <span className="tpv-term-res">[REQUIRED] Valid GitHub repository or live URL.</span>
                </div>
                <div className="tpv-term-line">
                  <span className="tpv-term-cmd">&gt; verify --readiness</span>
                  <span className="tpv-term-res">[{quizScore >= 70 ? 'PASS' : 'PENDING'}] AI Assessment threshold (70%).</span>
                </div>
              </div>
            </div>

            {project.requiredSkills?.length > 0 && (
              <div className="tpv-skills-section">
                <p className="tpv-mini-lbl">Tech Stack Requirements</p>
                <div className="tpv-skill-pills">
                  {project.requiredSkills.map(s => <span key={s} className="skill-pill">{s}</span>)}
                </div>
              </div>
            )}
          </section>

          <section className="tpv-card tpv-steps animate-slide-up" style={{ animationDelay: '0.2s' }}>
            <div className="card-lbl">EXECUTION LOG</div>
            <div className="tpv-steps-list">
              <div className="tpv-step">
                <span className="step-num">01</span>
                <div className="step-txt">
                  <strong>Architectural Review</strong>
                  <p>Review the curriculum materials for Phase {phase.order} to ensure conceptual alignment.</p>
                </div>
              </div>
              <div className="tpv-step">
                <span className="step-num">02</span>
                <div className="step-txt">
                  <strong>Code implementation</strong>
                  <p>Develop the core logic. Use standard Learnlia environment variables if required.</p>
                </div>
              </div>
              <div className="tpv-step">
                <span className="step-num">03</span>
                <div className="step-txt">
                  <strong>Deployment & Feedback</strong>
                  <p>Push to GitHub and verify your AI Concept Check score is above 70%.</p>
                </div>
              </div>
            </div>
          </section>
        </div>

        <aside className="tpv-sidebar">
          <div className="tpv-status-ring">
            <div className="status-item">
              <label>PHASE_STATUS</label>
              <span className={isDone ? 'done' : 'pending'}>{isDone ? 'ACKNOWLEDGED' : 'WAITING_SIGNAL'}</span>
            </div>
            <div className="status-item">
              <label>AI_ASSESSMENT</label>
              <span className={quizScore >= 70 ? 'done' : 'pending'}>{quizScore}%</span>
            </div>
          </div>

          <div className="tpv-submit-card">
            <div className="tpv-submit-header">
              <span className="tpv-submit-icon">🚀</span>
              <h3>Submit Mission</h3>
            </div>

            {isDone ? (
              <div className="tpv-current-submission">
                <label>ENCRYPTED_LINK</label>
                <a href={pp?.projectUrl} target="_blank" rel="noreferrer" className="tpv-ext-link">
                  View Submission ↗
                </a>
              </div>
            ) : (
              <form className="tpv-submit-flow" onSubmit={handleSubmit}>
                <p className="tpv-submit-hint">Provide the URL to your GitHub repository or live deployment for verification.</p>
                <div className="tpv-input-wrap">
                  <input
                    className="tpv-url-input"
                    type="url"
                    placeholder="https://github.com/user/project"
                    value={projectUrl}
                    onChange={(e) => setProjectUrl(e.target.value)}
                    required
                  />
                </div>
                <button
                  className="tpv-submit-btn"
                  type="submit"
                  disabled={submitting || (quizScore < 70)}
                >
                  {submitting ? 'TRANSMITTING...' : (quizScore < 70 ? 'PASS QUIZ FIRST' : 'CONFIRM SUBMISSION')}
                </button>
                {submitMsg && <p className={`tpv-error-text ${submitMsg.type}`}>{submitMsg.text}</p>}
              </form>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

export default TrackProjectView;
